import { useEffect, useRef, useState, type FormEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Modal } from '@/components/shared/Modal';

/**
 * Phase 12.4E — subscription lifecycle confirmation.
 *
 * Design of record: `SUBSCRIPTIONS_DESIGN.md` v1.4 §6.4, §8.2–§8.5.
 * Authorized by **D-109 §1**.
 *
 * Friction tracks reversibility, exactly as the Companies dialog established:
 *
 * | Action     | Reason     | Type-to-confirm |
 * |------------|------------|-----------------|
 * | Activate   | optional   | no              |
 * | Suspend    | MANDATORY  | no              |
 * | Reactivate | optional   | no              |
 * | Cancel     | MANDATORY  | YES — the company name |
 *
 * Suspend is reversible but takes a company's users offline, so it earns a
 * mandatory reason for the audit trail. Cancel is terminal (D-9), so it earns
 * the name typed out. Activate gets a plain confirm — ceremony where nothing is
 * at stake only teaches people to click through ceremony where something is.
 *
 * ── The reason is the ONLY field this dialog can produce ────────────────────
 * There is no period input, no expiry input, no plan select and no status
 * select, because none of them is a lifecycle input (OD-A, OD-B, D-12). The
 * backend would reject them anyway — it runs `forbidNonWhitelisted` — but a
 * field that does not exist cannot be sent by mistake.
 */

export type SubscriptionLifecycleAction = 'activate' | 'suspend' | 'reactivate' | 'cancel';

interface ActionCopy {
  title: string;
  /** Stated plainly, including the consequence where there is one. */
  body: string;
  confirmLabel: string;
  reasonRequired: boolean;
  typeToConfirm: boolean;
  destructive: boolean;
}

export const SUBSCRIPTION_LIFECYCLE_COPY: Record<SubscriptionLifecycleAction, ActionCopy> = {
  activate: {
    title: 'Activate subscription',
    body: 'This moves the subscription from Trial to Active. The company’s users already have access during Trial, so nothing changes for them today — this records the decision. No term or expiry date is set.',
    confirmLabel: 'Activate',
    reasonRequired: false,
    typeToConfirm: false,
    destructive: false,
  },
  suspend: {
    title: 'Suspend subscription',
    body: 'The company’s users will lose access on their next request. No data is deleted, and the subscription can be reactivated at any time.',
    confirmLabel: 'Suspend',
    reasonRequired: true,
    typeToConfirm: false,
    destructive: true,
  },
  reactivate: {
    title: 'Reactivate subscription',
    body: 'This restores access for the company’s users. Nothing is recreated — their data was retained throughout the suspension.',
    confirmLabel: 'Reactivate',
    reasonRequired: false,
    typeToConfirm: false,
    destructive: false,
  },
  cancel: {
    title: 'Cancel subscription',
    body: 'Cancellation is permanent and cannot be undone. The company’s users lose access and the subscription cannot be reactivated. The record is RETAINED — this is retirement, not deletion.',
    confirmLabel: 'Cancel subscription',
    reasonRequired: true,
    typeToConfirm: true,
    destructive: true,
  },
};

/** Mirrors the backend DTO so the user is not taught the rule by a 400. */
const REASON_MIN = 3;
const REASON_MAX = 500;

interface Props {
  action: SubscriptionLifecycleAction | null;
  companyName: string;
  submitting: boolean;
  error: string;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

export function SubscriptionLifecycleDialog({
  action,
  companyName,
  submitting,
  error,
  onClose,
  onConfirm,
}: Props) {
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  // Reset only when the ACTION changes — never on error. A 409 or 400 must
  // leave the operator's typed reason exactly where it was (§8.6); retyping it
  // is how a careful explanation turns into "see previous".
  useEffect(() => {
    if (action) {
      setReason('');
      setTyped('');
      const timer = setTimeout(() => reasonRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [action]);

  if (!action) return null;

  const copy = SUBSCRIPTION_LIFECYCLE_COPY[action];
  const trimmed = reason.trim();
  // Mandatory reasons must clear MinLength(3) AFTER trimming, so "  " fails
  // here rather than at the server.
  const reasonOk = copy.reasonRequired
    ? trimmed.length >= REASON_MIN && trimmed.length <= REASON_MAX
    : trimmed.length <= REASON_MAX;
  const nameOk = !copy.typeToConfirm || typed.trim() === companyName.trim();
  const canSubmit = reasonOk && nameOk && !submitting;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onConfirm(trimmed);
  };

  return (
    <Modal open onClose={submitting ? () => undefined : onClose} title={copy.title} size="md">
      <form onSubmit={submit} className="space-y-4 px-6 py-5">
        <p className="text-sm text-muted-foreground">{copy.body}</p>

        <div className="rounded-lg border border-border bg-muted/30 px-3 py-2">
          <p className="text-xs text-muted-foreground">Company</p>
          <p className="break-words text-sm font-medium text-foreground">{companyName}</p>
        </div>

        <div>
          <label
            htmlFor="subscription-lifecycle-reason"
            className="mb-1.5 block text-sm font-medium text-foreground"
          >
            Reason{' '}
            {copy.reasonRequired ? (
              <span className="text-destructive">*</span>
            ) : (
              <span className="text-muted-foreground">(optional)</span>
            )}
          </label>
          <textarea
            id="subscription-lifecycle-reason"
            ref={reasonRef}
            rows={3}
            value={reason}
            maxLength={REASON_MAX}
            onChange={(e) => setReason(e.target.value)}
            required={copy.reasonRequired}
            aria-required={copy.reasonRequired}
            placeholder={
              copy.reasonRequired
                ? 'Recorded on the subscription and in the platform audit trail — say why.'
                : 'Optional note for the audit trail.'
            }
            className="w-full resize-y rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {/* The clearing behaviour is a real side effect (OD-D), so it is
              stated rather than left for the operator to discover. */}
          {!copy.reasonRequired && (
            <p className="mt-1.5 text-xs text-muted-foreground">
              Leaving this blank clears any reason recorded previously.
            </p>
          )}
          {copy.reasonRequired && trimmed.length > 0 && trimmed.length < REASON_MIN && (
            <p className="mt-1.5 text-xs text-destructive">
              Give at least {REASON_MIN} characters.
            </p>
          )}
        </div>

        {copy.typeToConfirm && (
          <div>
            <label
              htmlFor="subscription-lifecycle-confirm-name"
              className="mb-1.5 block text-sm font-medium text-foreground"
            >
              Type <span className="font-semibold">{companyName}</span> to confirm
            </label>
            <input
              id="subscription-lifecycle-confirm-name"
              type="text"
              value={typed}
              autoComplete="off"
              onChange={(e) => setTyped(e.target.value)}
              className="h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:h-10"
            />
          </div>
        )}

        {error && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2"
          >
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive"
            />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Full-width, stacked below sm — Modal Standards. */}
        <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="min-h-11 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50 sm:min-h-0"
          >
            Keep as is
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className={
              copy.destructive
                ? 'inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground transition-colors hover:bg-destructive/90 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0'
                : 'inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0'
            }
          >
            {submitting && (
              <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin" />
            )}
            {copy.confirmLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
}
